import {
  IonContent,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSpinner,
  useIonViewDidEnter,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from '@ionic/react';
import React, { useLayoutEffect, useMemo, useState } from 'react';
import './Default.css';
import AppPageHeader from '../components/AppPageHeader';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router';
import useEnhancedAuth from '../EnhancedAuth';
import {
  FarecardPass,
  FareProduct,
  PaymentMethod,
} from '../services/types/apitypes';
import {
  apiApplePay,
  apiCardPasses,
  apiFareProducts,
  apiGetPaymentMethods,
  apiGooglePay,
  apiPurchase,
} from '../services/api';
import CardPassesList from '../usecases/cardpasses/CardPassesList';
import CardPassesAdd from '../usecases/cardpasses/CardPassesAdd';
import {
  getAppError,
  getAppSuccess,
  getUserMessageOrValidationError,
} from '../utils/messages';
import CardPassesPayTop from '../usecases/cardpasses/CardPassesPayTop';
import PaymentAddButton from '../usecases/payments/PaymentAddButton';
import AppSeparator from '../components/AppSeparator';
import PaymentAddProcess from '../usecases/shared/PaymentAddProcess';
import PlatformPayButton from '../usecases/payments/PlatformPayButton';
import PaymentsList, {AdditionalPaymentMethod} from '../usecases/payments/PaymentsList';
import PaymentMethodSelector from '../usecases/payments/PaymentMethodSelector';
import PaymentTotal from '../usecases/payments/PaymentTotal';
import {
  pickInitialPaymentMethod,
  rememberPaymentMethod,
} from '../usecases/payments/preferredPayment';
import CardPassesPayBottom from '../usecases/cardpasses/CardPassesPayBottom';
import { storageGetPasses, storageSetPasses } from '../utils/storage';
import {
  CallbackFnProps,
  defaultConfigure, getAdditionalPaymentMethods,
  getFieldsForPlatform,
  useCollectJS,
} from '../hooks/usecollectJS';
import { isPlatformButtonEnabled } from '../utils/config';
import { LoadingController } from '../utils/LoadingController';
import { useToaster } from '../hooks/useToaster';

// Unique selector so this page's platform button never collides with another
// page's that Ionic has left mounted in the DOM.
const PLATFORM_BUTTON_ID = 'passes-platform-button';
const fields = getFieldsForPlatform('#' + PLATFORM_BUTTON_ID);
// Platform + build-time flag only, so this is constant for the life of the app.
const HAS_ADDITIONAL_PAYMENTS = getAdditionalPaymentMethods().length > 0;

const CardPassesPage: React.FC = () => {
  const auth = useEnhancedAuth();
  const [t] = useTranslation('global');
  const router = useHistory();
  const [present] = useToaster();

  const { cardid } = useParams<{ cardid: string }>();

  const [block, setBlock] = useState<string>('list');
  const [passes, setPasses] = useState<FarecardPass[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    PaymentMethod | AdditionalPaymentMethod | undefined
  >(undefined);
  const [products, setProducts] = useState<FareProduct[]>([]);

  const [selectedProduct, setSelectedProduct] = useState<
    FareProduct | undefined
  >(undefined);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [isPageActive, setIsPageActive] = useState(false);
  useIonViewDidEnter(() => {
    setIsPageActive(true);
  });
  useIonViewWillLeave(() => {
    setIsPageActive(false);
  });

  const collectJsConfig = useMemo(
    () => ({
      ...defaultConfigure,
      // `price` is what Collect.js puts on the Apple/Google Pay sheet: dollars as a
      // decimal string. FareProduct.price is cents, so it must be divided by 100 —
      // passing it raw made a $3.00 pass ask the wallet for $300.00.
      price: ((selectedProduct?.price ?? 0) / 100).toFixed(2),
      currency: 'USD',
      country: 'US',
      fields,
      callback: async (e: CallbackFnProps) => {
        try {
          LoadingController.show();
          setIsSubmitting(true);

          if ('applePay' in fields) {
            await apiApplePay(
              {
                products: [
                  {
                    offerRef: selectedProduct?.offerReference,
                    quantity: 1,
                  },
                ],
                cardNetwork: e.wallet.cardNetwork,
                encryptedPaymentData: "",
                paymentToken: e.token
              },
              auth,
            );
          } else {
            await apiGooglePay(
              {
                isStoredValuePayment: false,
                partialPayment: [],
                products: [
                  {
                    offerRef: selectedProduct?.offerReference,
                    quantity: 1,
                  },
                ],
                paymentToken: e.token,
              },
              auth,
            );
          }
          rememberPaymentMethod('applePay' in fields ? 'APPLE' : 'GOOGLE');
          setBlock('list');
          present(getAppSuccess(t('cardpasses.purchasesuccess')));
        } catch (error) {
          console.error('Error adding value ', error);
          const apiError = getUserMessageOrValidationError(error);
          if (typeof apiError === 'string') {
            present(getAppError(apiError));
          } else {
            present(getAppError(t('cardpasses.purchasenosuccess')));
          }
        } finally {
          setIsSubmitting(false);
          LoadingController.hide();
        }
      },
    }),
    [selectedProduct, auth, t],
  );

  const collectJs = useCollectJS(collectJsConfig);

  useLayoutEffect(() => {
    if (collectJs) {
      collectJs.configure();
    }
  }, [collectJs, block]);

  // dont use useEffect, it does not trigger the second time a page with the same parameter is loaded.
  // we want the page to reload the current data every time it is opened
  useIonViewWillEnter(() => {
    setBlock('list');
    const storedPasses = storageGetPasses(cardid);
    if (storedPasses) {
      setPasses(storedPasses);
    }
    loadPasses().then(() => console.log('Loaded passes'));
  }, [auth.isLoading]);

  const refresh = (e: CustomEvent) => {
    loadPasses().then(() => e.detail.complete());
  };

  const loadPasses = async () => {
    try {
      if (!passes) {
        LoadingController.show();
      }
      const result = await apiCardPasses(cardid, auth);
      setPasses(result.data);
      storageSetPasses(cardid, result.data);
    } catch (error) {
      console.log('Error loading passes', error);
      const apiError = getUserMessageOrValidationError(error);
      if (typeof apiError === 'string') {
        present(getAppError(apiError));
      } else {
        present(getAppError(t('cardpasses.passesnosuccess')));
      }
    } finally {
      LoadingController.hide();
    }
  };

  const addPaymentMethod = () => {
    setBlock('addpayment');
  };

  const selectPaymentMethod = (p: PaymentMethod | AdditionalPaymentMethod) => {
    setSelectedPaymentMethod(p);
    setBlock('pay');
  };

  const chosePayments = () => {
    setBlock('selectpayment');
  };

  // With wallets off there is nothing to choose between: the user has no
  // saved cards (or selectedPaymentMethod would be set) and no wallets, so
  // send them straight to the card form as before.
  const choosePaymentOrAdd = () =>
    HAS_ADDITIONAL_PAYMENTS ? chosePayments() : addPaymentMethod();

  const goToAdd = async () => {
    await loadProducts();
  };

  const goToPay = async (p: FareProduct) => {
    setSelectedProduct(p);
    await loadPayments();
    setBlock('pay');
  };

  const loadProducts = async () => {
    try {
      LoadingController.show();
      const result = await apiFareProducts(cardid);
      setProducts(result.data);
      setBlock('add');
    } catch (error) {
      console.log('Error loading passes', error);
      const apiError = getUserMessageOrValidationError(error);
      if (typeof apiError === 'string') {
        present(getAppError(apiError));
      } else {
        present(getAppError(t('cardpasses.productsnosuccess')));
      }
      setBlock('list');
    } finally {
      LoadingController.hide();
    }
  };

  const loadPayments = async () => {
    try {
      LoadingController.show();
      const response = await apiGetPaymentMethods(auth);
      setPaymentMethods(response.data);
      const initial = pickInitialPaymentMethod(response.data, getAdditionalPaymentMethods());
      if (initial) {
        setSelectedPaymentMethod(initial);
      }
      return response.data;
    } catch (error) {
      console.log('Error loading payments', error);
      const apiError = getUserMessageOrValidationError(error);
      if (typeof apiError === 'string') {
        present(getAppError(apiError));
      } else {
        present(getAppError(t('cardpasses.paymentsnosuccess')));
      }
    } finally {
      LoadingController.hide();
    }
  };

  const returnFromAddPayment = async () => {
    const payments = await loadPayments();
    // Nothing to choose between — the user cancelled the form, has no saved
    // cards, and wallets are off. Go back where they started instead of
    // showing an empty picker.
    const nothingToChoose = (payments?.length ?? 0) === 0 && !HAS_ADDITIONAL_PAYMENTS;
    setBlock(nothingToChoose ? 'pay' : 'selectpayment');
  };

  const completePurchaseWithMobilePay = async (
      paymentMethod: PaymentMethod,
      product: FareProduct,) => {


    try {
      LoadingController.show();
      setIsSubmitting(true);

    } catch (e) {
      // ...
    }

  }

  const completePurchase = async (
    paymentMethod: PaymentMethod,
    product: FareProduct,
  ) => {
    if (isSubmitting) {
      return;
    }

    try {
      LoadingController.show();
      setIsSubmitting(true);

      await apiPurchase(
        {
          isStoredValuePayment: false,
          partialPayment: [],
          paymentMethodId: paymentMethod.id,
          products: [
            {
              offerRef: product.offerReference,
              quantity: 1,
            },
          ],
        },
        auth,
      );
      rememberPaymentMethod(paymentMethod);
      await loadPasses();
      setBlock('list');
      present(getAppSuccess(t('cardpasses.purchasesuccess')));
    } catch (error) {
      console.error('Error adding value ', error);
      const apiError = getUserMessageOrValidationError(error);
      if (typeof apiError === 'string') {
        present(getAppError(apiError));
      } else {
        present(getAppError(t('cardpasses.purchasenosuccess')));
      }
    } finally {
      setIsSubmitting(false);
      LoadingController.hide();
    }
  };

  const goBack = () => {
    router.push('/card/' + cardid + '/options');
  };

  return (
    <IonPage id="farecardpasses-page">
      {block === 'list' && (
        <>
          <AppPageHeader
            isPageActive={isPageActive}
            back={() => goBack()}
            title={t('cardpasses.title')}
          ></AppPageHeader>
          <IonContent fullscreen className="ion-padding default-content">
            <IonRefresher slot="fixed" onIonRefresh={refresh}>
              <IonRefresherContent></IonRefresherContent>
            </IonRefresher>
            <CardPassesList add={() => goToAdd()} passes={passes} />
          </IonContent>
        </>
      )}
      {block === 'addpayment' && (
        <PaymentAddProcess
          isPageActive={isPageActive}
          callback={returnFromAddPayment}
        />
      )}
      {block === 'selectpayment' && (
        <>
          <AppPageHeader
            isPageActive={isPageActive}
            back={() => setBlock('pay')}
            title={t('cardpasses.title')}
          ></AppPageHeader>
          <IonContent fullscreen className="ion-padding default-content">
            <PaymentsList
              payments={paymentMethods}
              selectPayment={selectPaymentMethod}
              additionalPayments={getAdditionalPaymentMethods()}
            />
            <AppSeparator />
            <PaymentAddButton click={addPaymentMethod} />
          </IonContent>
        </>
      )}
      {block === 'pay' && (
        <>
          <AppPageHeader
            isPageActive={isPageActive}
            back={() => setBlock('add')}
            title={t('cardpasses.title')}
          ></AppPageHeader>
          <IonContent fullscreen className="ion-padding default-content">
            {selectedProduct && <CardPassesPayTop product={selectedProduct} />}
            <AppSeparator />
            {selectedPaymentMethod && typeof selectedPaymentMethod !== "string" && selectedProduct ? (
              <CardPassesPayBottom
                paymentMethod={selectedPaymentMethod}
                chosePayments={chosePayments}
                product={selectedProduct}
                finish={completePurchase}
                collectJs={collectJs}
              />
            ) : (
              <>
                {typeof selectedPaymentMethod === 'string' ? (
                  <>
                    <PaymentMethodSelector payment={selectedPaymentMethod} click={chosePayments} />
                    {selectedProduct && <PaymentTotal amount={selectedProduct.price / 100} />}
                  </>
                ) : (
                  <PaymentAddButton
                    click={choosePaymentOrAdd}
                    labelKey={HAS_ADDITIONAL_PAYMENTS ? 'payments.choose' : 'payments.add'}
                  />
                )}
                {isPlatformButtonEnabled() && (
                  <PlatformPayButton id={PLATFORM_BUTTON_ID}>
                    {typeof selectedPaymentMethod === 'string' && <IonSpinner />}
                  </PlatformPayButton>
                )}
              </>
            )}
          </IonContent>
        </>
      )}
      {block === 'add' && (
        <>
          <AppPageHeader
            isPageActive={isPageActive}
            back={() => setBlock('list')}
            title={t('cardpasses.title')}
          ></AppPageHeader>
          <IonContent fullscreen className="ion-padding default-content">
            <CardPassesAdd
              pay={(p: FareProduct) => goToPay(p)}
              products={products}
            />
          </IonContent>
        </>
      )}
    </IonPage>
  );
};

export default CardPassesPage;
